/**
 * 标定参数-批量设置表单
 */
import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Form, InputNumber, Row, Col, Radio, Modal } from 'antd';
import { formSearchGutter } from '@/constants/common';
import { filterFormValues } from '@/utils/useFormToJsonString';
import { SubDeviceTypeEnum } from '@/constants/deviceManage';

export interface CalibrationParamsWriteFormRef {
  getSubmitString: () => Promise<string | null>;
}

// 定义标定类型选项及其对应的设备ID
const calibrationTypes = [
  { 
    label: '前向一体机标定结果(多来目)', 
    value: 'camera_dlm',
    deviceId: SubDeviceTypeEnum.forwardIntegratedMachine_duolaimu 
  },
  { 
    label: '相机标定结果(园之云)', 
    value: 'camera_yzy',
    deviceId: SubDeviceTypeEnum.forwardIntegratedMachine_camera_yuanzhiyun 
  },
  { 
    label: '雷达标定结果(园之云)', 
    value: 'radar_yzy',
    deviceId: SubDeviceTypeEnum.forwardIntegratedMachine_radar_yuanzhiyun 
  },
  { 
    label: '拉线电机标定结果', 
    value: 'motor_zdw',
    deviceId: SubDeviceTypeEnum.wireMotor 
  },
  { 
    label: '组合惯导标定结果(阿奇)', 
    value: 'ins_aqi',
    deviceId: SubDeviceTypeEnum.ins_aqi 
  },
  {
    label: '安行前向相机标定结果',
    value: 'ax_forward_camera',
    deviceId: SubDeviceTypeEnum.ax_forward_camera
  },
  {
    label: '安行前向毫米波雷达标定结果',
    value: 'ax_forward_mmw_radar',
    deviceId: SubDeviceTypeEnum.ax_forward_mmw_radar
  },
  {
    label: '安行DMS相机标定结果',
    value: 'ax_dms_camera',
    deviceId: SubDeviceTypeEnum.ax_dms_camera
  },
  {
    label: '安行BSD相机标定结果',
    value: 'ax_bsd_camera',
    deviceId: SubDeviceTypeEnum.ax_bsd_camera
  },
];

const CalibrationParamsWriteForm = forwardRef<CalibrationParamsWriteFormRef, {}>((props, ref) => {
  const [form] = Form.useForm();
  const [selectedType, setSelectedType] = useState<string>('camera_dlm');

  // 检查当前表单是否有值
  const hasFormValues = () => {
    const values = form.getFieldsValue();
    return Object.keys(values).some(key => {
      const value = values[key];
      if (value && typeof value === 'object') {
        return Object.values(value).some(v => v !== undefined && v !== null);
      }
      return value !== undefined && value !== null;
    });
  };

  // 导出方法：获取表单数据的JSON字符串
  useImperativeHandle(ref, () => ({
    getSubmitString: async () => {
      // 先检查表单是否有值
      if (!hasFormValues()) {
        return null;
      }

      const values = await form.validateFields();
      const filteredValues = filterFormValues(values);
      
      // 如果过滤后没有有效值，返回null
      if (filteredValues === null) {
        return null;
      }

      // 解析JSON并添加id字段
      const parsedValues = JSON.parse(filteredValues);
      
      // 获取当前选中的标定类型对应的设备ID
      const currentType = calibrationTypes.find(type => type.value === selectedType);
      if (currentType) {
        parsedValues.id = currentType.deviceId;
      }

      return JSON.stringify(parsedValues);
    }
  }));

  // 切换标定类型
  const handleTypeChange = (e: any) => {
    const newType = e.target.value;
    
    if (hasFormValues()) {
      Modal.confirm({
        title: '确认切换',
        content: '切换后当前表单数据将被清空，确认要切换吗？',
        okText: '确认',
        cancelText: '取消',
        onOk: () => {
          form.resetFields();
          setSelectedType(newType);
        },
      });
    } else {
      setSelectedType(newType);
    }
  };

  return (
    <Form form={form}>
      {/* 标定类型选择按钮组 */}
      <div style={{ marginBottom: 24 }}>
        <Radio.Group 
          value={selectedType} 
          onChange={handleTypeChange}
          buttonStyle="solid"
        >
          {calibrationTypes.map(type => (
            <Radio.Button key={type.value} value={type.value}>
              {type.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>
      {/* 前向一体机标定结果 - 多来目 */}
      {selectedType === 'camera_dlm' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item 
              label="标定状态" 
              name="success"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入标定状态" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="镜头高度(米)" 
              name="cameraMountingHeight"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入镜头高度" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="设备至车头距离(米)" 
              name="frontBumperDistance"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入距离" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="设备至车右侧距离(米)" 
              name="rightSideDistance"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入距离" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="设备至车左侧距离(米)" 
              name="leftSideDistance"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入距离" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 相机标定结果 - 园之云 */}
      {selectedType === 'camera_yzy' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item 
              label="标定状态" 
              name="success"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入标定状态" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="摄像头到前保的纵向距离(米)" 
              name="cameraFrontBumperDistance"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入距离(负值)" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="摄像头中线距离(米)" 
              name="cameraCenterOffset"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="左正右负" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="摄像头到地面距离(米)" 
              name="cameraHeight"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入高度" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="标定板图案中心高度(米)" 
              name="calibrationBoardCenterHeight"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入高度" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置x"
              name="locationX"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置x" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置y"
              name="locationY"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置y" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置z"
              name="locationZ"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置z" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后pitch"
              name="pitch"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入pitch" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后yaw"
              name="yaw"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入yaw" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后roll"
              name="roll"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入roll" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 雷达标定结果 - 园之云 */}
      {selectedType === 'radar_yzy' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item 
              label="后轴到雷达距离(米)" 
              name="xOffset"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入距离" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="雷达中心偏离(米)" 
              name="yOffset"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="左正右负" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="雷达中心离地高度(米)" 
              name="zOffset"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入高度" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 拉线电机标定结果 */}
      {selectedType === 'motor_zdw' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item 
              label="标定状态" 
              name="success"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入标定状态" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="C3" 
              name="c3"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入C3" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="C2" 
              name="c2"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入C2" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="C1" 
              name="c1"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入C1" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label="C0" 
              name="c0"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入C0" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="静态标定: 空行程"
              name="pedalIdleTravel"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入空行程" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="静态标定: 最大行程"
              name="pedalMaxTravel"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入最大行程" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="静态标定-x^1系数"
              name="staticC1"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入x^1系数" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="静态标定-常数项系数"
              name="staticC0"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入常数项系数" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 组合惯导标定结果 */}
      {selectedType === 'ins_aqi' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item 
              label="标定状态" 
              name="success"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入标定状态" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 安行前向相机标定结果 */}
      {selectedType === 'ax_forward_camera' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item
              label="标定状态"
              name="success"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入标定状态" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置x"
              name="locationX"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置x" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置y"
              name="locationY"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置y" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置z"
              name="locationZ"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置z" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后pitch"
              name="pitch"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入pitch" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后yaw"
              name="yaw"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入yaw" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后roll"
              name="roll"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入roll" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 安行前向毫米波雷达标定结果 */}
      {selectedType === 'ax_forward_mmw_radar' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item
              label="标定状态"
              name="success"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入标定状态" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置x"
              name="locationX"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置x" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置y"
              name="locationY"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置y" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置z"
              name="locationZ"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置z" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后pitch"
              name="pitch"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入pitch" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后yaw"
              name="yaw"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入yaw" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后roll"
              name="roll"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入roll" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 安行DMS相机标定结果 */}
      {selectedType === 'ax_dms_camera' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item
              label="标定状态"
              name="success"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入标定状态" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="headPitch"
              name="headPitch"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入headPitch" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="headYaw"
              name="headYaw"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入headYaw" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="headRoll"
              name="headRoll"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入headRoll" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="gazePitch"
              name="gazePitch"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入gazePitch" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="gazeYaw"
              name="gazeYaw"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入gazeYaw" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 安行BSD相机标定结果 */}
      {selectedType === 'ax_bsd_camera' && (
        <Row gutter={formSearchGutter}>
          <Col span={8}>
            <Form.Item
              label="标定状态"
              name="success"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入标定状态" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置x"
              name="locationX"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置x" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置y"
              name="locationY"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置y" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后位置z"
              name="locationZ"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入位置z" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后pitch"
              name="pitch"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入pitch" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后yaw"
              name="yaw"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入yaw" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="标定后roll"
              name="roll"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入roll" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="imgW"
              name="imgW"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入imgW" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="imgH"
              name="imgH"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入imgH" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="px"
              name="px"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入px" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="py"
              name="py"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入py" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="fx"
              name="fx"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入fx" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="fy"
              name="fy"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入fy" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="cx"
              name="cx"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入cx" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="cy"
              name="cy"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入cy" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="k1"
              name="k1"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入k1" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="k2"
              name="k2"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入k2" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="k3"
              name="k3"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入k3" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="k4"
              name="k4"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入k4" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="mountMode"
              name="mountMode"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入mountMode" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="calibrationFieldVersion"
              name="calibrationFieldVersion"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入calibrationFieldVersion" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="cameraModel"
              name="cameraModel"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入cameraModel" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="detectionMode"
              name="detectionMode"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入detectionMode" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="vehicleCoordOrigin"
              name="vehicleCoordOrigin"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入vehicleCoordOrigin" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="reserved"
              name="reserved"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="请输入reserved" />
            </Form.Item>
          </Col>
        </Row>
      )}
    </Form>
  );
});

export default CalibrationParamsWriteForm;
